import React from 'react';
import { GraduationCap, Plus, Trash2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import type { Education as EducationType } from '../types';
import { DateInput } from '../components/DateInput';

const defaultEducation: Omit<EducationType, 'id'> = {
  institution: '',
  degree: '',
  fieldOfStudy: '',
  startDate: '',
  endDate: '',
  isPursuing: false,
  grade: '',
  achievements: ''
};

interface EducationProps {
  education: EducationType[];
  setEducation: React.Dispatch<React.SetStateAction<EducationType[]>>;
}

export function Education({ education, setEducation }: EducationProps) {
  const addEducation = () => {
    setEducation([...education, {
      ...defaultEducation,
      id: Math.max(0, ...education.map(e => e.id)) + 1
    }]);
  };

  const removeEducation = (id: number) => {
    setEducation(education.filter(edu => edu.id !== id));
  };

  const updateEducation = (id: number, field: keyof EducationType, value: any) => {
    setEducation(education.map(edu => 
      edu.id === id ? { ...edu, [field]: value } : edu
    ));
  };

  return (
    <motion.div 
      className="space-y-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      <h2 className="text-2xl font-semibold flex items-center gap-2">
        <GraduationCap className="w-5 h-5" />
        Education
      </h2>

      <AnimatePresence>
        {education.map((edu) => (
          <motion.div
            key={edu.id}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="space-y-4 p-4 border border-gray-200 rounded-lg hover:border-indigo-300 
              transition-colors duration-200"
          >
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Institution</label>
                <input
                  type="text"
                  value={edu.institution || ''}
                  onChange={(e) => updateEducation(edu.id, 'institution', e.target.value)}
                  required
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm 
                    focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Degree/Course</label>
                <input
                  type="text"
                  value={edu.degree || ''}
                  onChange={(e) => updateEducation(edu.id, 'degree', e.target.value)}
                  required
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm 
                    focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Field of Study</label>
                <input
                  type="text"
                  value={edu.fieldOfStudy || ''}
                  onChange={(e) => updateEducation(edu.id, 'fieldOfStudy', e.target.value)}
                  placeholder="e.g., Computer Science"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm 
                    focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Grade/CGPA (Optional)</label>
                <input
                  type="text"
                  value={edu.grade || ''}
                  onChange={(e) => updateEducation(edu.id, 'grade', e.target.value)}
                  placeholder="e.g., 3.8/4.0 or First Class"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm 
                    focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                />
              </div>

              <DateInput
                label="Start Date"
                value={edu.startDate || ''}
                onChange={(value) => updateEducation(edu.id, 'startDate', value)}
                required
              />

              <DateInput
                label="End Date"
                value={edu.endDate || ''}
                onChange={(value) => updateEducation(edu.id, 'endDate', value)}
                showPursuing
                isPursuing={edu.isPursuing}
                onPursuingChange={(value) => {
                  updateEducation(edu.id, 'isPursuing', value);
                  if (value) {
                    updateEducation(edu.id, 'endDate', '');
                  }
                }}
              />

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700">Achievements/Activities</label>
                <textarea
                  value={edu.achievements || ''}
                  onChange={(e) => updateEducation(edu.id, 'achievements', e.target.value)}
                  placeholder="List relevant achievements, activities, or honors"
                  rows={3}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm 
                    focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                />
              </div>
            </div>

            {education.length > 1 && (
              <motion.button
                type="button"
                onClick={() => removeEducation(edu.id)}
                className="text-red-600 hover:text-red-700 flex items-center gap-1"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Trash2 className="w-4 h-4" />
                Remove Education
              </motion.button>
            )}
          </motion.div>
        ))}
      </AnimatePresence>

      <motion.button
        type="button"
        onClick={addEducation}
        className="flex items-center gap-1 text-indigo-600 hover:text-indigo-700"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        <Plus className="w-4 h-4" />
        Add Another Education
      </motion.button>
    </motion.div>
  );
}